import type { Revision, RevisionPage } from '../../types';
import { isYValue, jsonToY, mutations } from '../../utils';
import { getPageParentInRevision } from './getPageParentInRevision';
import { isRevisionPageParent } from './guards';

export interface InsertPageInRevisionOptions {
    /**
     * The id of the page to insert into.
     * If not defined, the page will be inserted as a root page.
     */
    into?: string;

    /**
     * The index to insert the page at.
     * If not defined, the page will be appended at the end.
     */
    at?: number;
}

/**
 * Insert a page into a revision.
 *
 * @param revision - The revision to insert the page into.
 * @param page - The page to insert.
 * @param options - The insertion options (see InsertPageInRevisionOptions)
 */
export function insertPageInRevision(
    revision: mutations.MutableData<Revision>,
    page: RevisionPage,
    options: InsertPageInRevisionOptions = {}
): void {
    let pagesList: mutations.MutableData<string[]> | undefined;
    if (options.into) {
        const parent = mutations.getIn(revision, ['pages', options.into] as const);
        if (!parent || !isRevisionPageParent(parent)) {
            throw new Error(`Cannot insert page into ${options.into}`);
        }

        pagesList = mutations.get(parent, 'pages');
    } else {
        pagesList = mutations.getIn(revision, ['rootPages'] as const);
    }

    if (!pagesList) {
        throw new Error(`Cannot insert page into ${options.into}`);
    }

    if (options.at !== undefined) {
        mutations.insert(pagesList, options.at, [page.id]);
    } else {
        mutations.push(pagesList, [page.id]);
    }

    mutations.setIn(
        revision,
        ['pages', page.id] as const,
        isYValue(revision) ? jsonToY(page) : page
    );
}

/**
 * Returns the insert page options for insertPageBeforeInRevision & insertPageAfterInRevision ops.
 */
function getInsertPageInRevisionOptions(
    revision: mutations.MutableData<Revision>,
    afterOrBefore: 'after' | 'before',
    targetPageId: string
): InsertPageInRevisionOptions {
    const parent = getPageParentInRevision(revision, targetPageId);
    const pagesArray = parent
        ? mutations.get(parent, 'pages')
        : mutations.get(revision, 'rootPages');

    const pageIndex = mutations.findIndex(pagesArray, (pageId) => pageId === targetPageId);

    if (pageIndex < 0) {
        return {
            at: pageIndex,
        };
    }

    const intoPageId = parent ? mutations.get(parent, 'id') : undefined;
    return afterOrBefore === 'after'
        ? {
              into: intoPageId,
              at: pageIndex + 1,
          }
        : {
              into: intoPageId,
              at: pageIndex,
          };
}

/**
 * Insert a page before another one.
 */
export function insertPageBeforeInRevision(
    revision: mutations.MutableData<Revision>,
    page: RevisionPage,
    beforePageId: string
): void {
    const insertPageOptions = getInsertPageInRevisionOptions(revision, 'before', beforePageId);

    if (insertPageOptions.at === undefined || insertPageOptions?.at < 0) {
        return;
    }

    insertPageInRevision(revision, page, insertPageOptions);
}

/**
 * Insert a page after another one.
 */
export function insertPageAfterInRevision(
    revision: mutations.MutableData<Revision>,
    page: RevisionPage,
    afterPageId: string
): void {
    const insertPageOptions = getInsertPageInRevisionOptions(revision, 'after', afterPageId);
    if (insertPageOptions.at === undefined || insertPageOptions.at < 0) {
        return;
    }

    insertPageInRevision(revision, page, insertPageOptions);
}
